import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
export function useCollectionMotion(
  root: RefObject<HTMLElement | null>,
  signature: string,
  options: { maxAnimated?: number; excludeSelector?: string } = {}
) {
  const positions = useRef(new Map<string, DOMRect>());
  useLayoutEffect(() => {
    const elements = Array.from(
      root.current?.querySelectorAll<HTMLElement>('[data-binder-key]') || []
    ).filter((el) => !options.excludeSelector || !el.closest(options.excludeSelector));
    const next = new Map<string, DOMRect>();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cap =
      options.maxAnimated ?? (window.innerWidth < 768 ? 4 : window.innerWidth < 1200 ? 6 : 8);
    let animated = 0;
    elements.forEach((el) => {
      const key = el.dataset.binderKey!;
      const rect = el.getBoundingClientRect();
      next.set(key, rect);
      const old = positions.current.get(key);
      const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
      const animateOffscreen = options.maxAnimated === Infinity;
      if (!reduced && (inViewport || animateOffscreen) && animated < cap && el.animate) {
        el.getAnimations().forEach((a) => a.cancel());
        if (old && (Math.abs(old.x - rect.x) > 1 || Math.abs(old.y - rect.y) > 1))
          el.animate(
            [
              {
                transform: `translate(${old.x - rect.x}px,${old.y - rect.y}px) scale(${old.width / Math.max(1, rect.width)})`,
              },
              { transform: 'translate(0,0) scale(1)' },
            ],
            { duration: 440, easing: 'cubic-bezier(.22,1,.36,1)' }
          );
        else if (!old)
          el.animate(
            [
              { opacity: 0, transform: 'translateY(12px) rotate(-2deg)' },
              { opacity: 1, transform: 'none' },
            ],
            {
              duration: 400,
              delay: Math.min(animated, 18) * 25,
              easing: 'cubic-bezier(.22,1,.36,1)',
            }
          );
        animated++;
      }
    });
    positions.current = next;
  }, [options.excludeSelector, options.maxAnimated, root, signature]);
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const update = () => {
      frame = 0;
      const r = el.getBoundingClientRect();
      const p = media.matches
        ? 1
        : Math.min(1, Math.max(0, -r.top / Math.max(200, window.innerHeight * 0.55)));
      el.style.setProperty('--binder-progress', String(p));
    };
    const tick = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    media.addEventListener('change', tick);
    update();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
      media.removeEventListener('change', tick);
    };
  }, [root]);
}
